/**
 * The RFI loop (wave W2, Mahmoud's R10): the register, the RFI screen and its date
 * agreement.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Nothing here decides who may do what: the server answers `availableMoves` per caller,
 * and a counterparty receives an empty inspector list by design (it never learns who is
 * coming).
 */

import type { TargetAttachment } from "./attachments";
import { apiJson, listBody, optStr, query, str, strArray } from "./client";
import { parseClassification } from "./credentials";

/** Whose turn it is on an RFI's date. */
export type RfiDateState =
  "DRAFT" | "AWAITING_OPERATOR" | "AWAITING_CONTRACTOR" | "AGREED" | "CLOSED_WITHOUT_DATE";

const DATE_STATES: readonly RfiDateState[] = [
  "DRAFT",
  "AWAITING_OPERATOR",
  "AWAITING_CONTRACTOR",
  "AGREED",
  "CLOSED_WITHOUT_DATE",
];

export type RfiMove = "ACKNOWLEDGE" | "PROPOSE" | "CONFIRM";

/** Which side moves next on a request; null once it is over. */
export type RfiSide = "OPERATOR" | "CONTRACTOR";

export interface RfiSummary {
  /** Wave Experience A: an open act of mine is about this request. */
  actionableByMe?: boolean;
  /** Wave Experience A, ruling B: the side that moves next, as the server reads it. */
  movesNext?: RfiSide | null;
  /** Wave Experience A, ruling B: the side that moves next is mine ("waiting on you"). */
  waitingOnMySide?: boolean;
  id: string;
  humanId: string;
  scope: string;
  inspectionType: string;
  location: string | null;
  plannedDate: string;
  workflowState: string;
  outcome: string | null;
  contractId: string;
  contractHumanId: string;
  contractorPartyId: string;
  contractorPartyName: string;
  /** Null on the operator's own request: no plan stands behind it. */
  itpId: string | null;
  itpHumanId: string | null;
  itpItemId: string | null;
  itemNo: string | null;
  /** RFI (the contractor asked) or OPERATOR_REQUEST (sent from a surveillance program). */
  origin: string;
  verificationTypeCode: string;
  verificationTypeLabel: string;
  disciplineLabel: string | null;
  dateState: RfiDateState;
  pendingDate: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  assigneePersonId: string | null;
  assigneeDisplayName: string | null;
  /**
   * Wave INST ruling 1: the instance this request covers (its location, tag, lot or
   * unit). Null on a request raised before instance labels: the whole item.
   */
  instanceLabel: string | null;
  /** Wave INST ruling 1: how much of the instance the request covers (default 1). */
  quantity: string;
  /** Wave INST ruling 1: the asset id in the operator's completions tool, when used. */
  externalAssetId: string | null;
  /** Wave INST ruling 3: the contractor's own internal check behind the declaration. */
  internalCheckReference: string | null;
  /** Wave INST ruling 3: raised inside the notice lead time, with a justification. */
  shortNotice: boolean;
  shortNoticeJustification: string | null;
  /**
   * Wave COM item 3: when the scheduler marked an agreed witness request unattended (the
   * operator did not start conduct by the agreed day plus the tenant's grace), and the
   * contractor's release of the witness point after it.
   */
  operatorUnattendedAt: string | null;
  witnessReleased: boolean;
  witnessReleasedAt: string | null;
  /** Wave RFI rulings 1 and 5: how many items the request covers, and its files. */
  itemCount: number;
  attachmentCount: number;
}

/** Wave RFI ruling 1: one item of a several-item request, each its own instance. */
export interface RfiItem {
  itpItemId: string;
  itemNo: string;
  instanceLabel: string | null;
  quantity: string;
  position: number;
}

export interface RfiHistoryEntry {
  id: string;
  at: string;
  kind: "TRANSITION" | "DATE";
  event: string;
  fromState: string | null;
  toState: string | null;
  proposedDate: string | null;
  text: string | null;
  actorDisplayName: string | null;
  actingPartyName: string | null;
}

export interface PersonOption {
  personId: string;
  displayName: string;
}

export interface RfiDetail extends RfiSummary {
  history: RfiHistoryEntry[];
  availableMoves: RfiMove[];
  inspectorOptions: PersonOption[];
  assigneeOptions: PersonOption[];
  /** Whether THIS caller may reject the contractor's request with a reason. */
  mayReject: boolean;
  /** Whether THIS caller may cancel the contractor's request. */
  mayCancel: boolean;
  /** Whether THIS caller may waive the contractor's request. */
  mayWaive: boolean;
  /** The conduct acts this caller may take on the agreed inspection from here. */
  conductActs: string[];
  /** Attachments linked to this request, visible to both parties. */
  attachments: TargetAttachment[];
  /**
   * Wave INST ruling 4: the waiver in two hands. PENDING_AUTHORITY once an operator
   * proposed it, AUTHORISED once the engineering authority completed it; null when no
   * waiver was proposed, and always null for a counterparty (the proposal is internal).
   */
  waiverState: string | null;
  /** The proposer's reason, shown to the authority who completes it. */
  waiverReason: string | null;
  waiverProposedAt: string | null;
  /** The caller proposed the waiver, so completing it is another person's act. */
  waiverProposedByMe: boolean;
  /** Wave COM item 3: the contractor may release the unattended witness point now. */
  mayReleaseWitness: boolean;
  /** The contractor's own declaration of what was inspected and found, once released. */
  witnessReleaseDeclaration: string | null;
  /** Wave RFI ruling 4: the caller may name or change the one assignee. */
  mayAssign: boolean;
  /** Wave RFI ruling 1: the items the request covers, in their order. */
  items: RfiItem[];
  /** Wave RFI ruling 5: the request's files by id (the attachments carry their names). */
  attachmentIds: string[];
}

function record(raw: unknown): Record<string, unknown> {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("Expected an object body");
  }
  return raw as Record<string, unknown>;
}

function dateState(raw: unknown): RfiDateState {
  return DATE_STATES.find((one) => one === raw) ?? "DRAFT";
}

function side(raw: unknown): RfiSide | null {
  return raw === "OPERATOR" || raw === "CONTRACTOR" ? raw : null;
}

function summary(body: Record<string, unknown>): RfiSummary {
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    scope: str(body.scope),
    inspectionType: str(body.inspection_type),
    location: optStr(body.location),
    plannedDate: str(body.planned_date),
    workflowState: str(body.workflow_state),
    outcome: optStr(body.outcome),
    contractId: str(body.contract_id),
    contractHumanId: str(body.contract_human_id),
    contractorPartyId: str(body.contractor_party_id),
    contractorPartyName: str(body.contractor_party_name),
    itpId: optStr(body.itp_id),
    itpHumanId: optStr(body.itp_human_id),
    itpItemId: optStr(body.itp_item_id),
    itemNo: optStr(body.item_no),
    origin: optStr(body.origin) ?? "RFI",
    verificationTypeCode: optStr(body.verification_type_code) ?? "",
    verificationTypeLabel: optStr(body.verification_type_label) ?? "",
    disciplineLabel: optStr(body.discipline_label),
    dateState: dateState(body.date_state),
    pendingDate: optStr(body.pending_date),
    submittedAt: optStr(body.submitted_at),
    approvedAt: optStr(body.approved_at),
    assigneePersonId: optStr(body.assignee_person_id),
    assigneeDisplayName: optStr(body.assignee_display_name),
    actionableByMe: body.actionable_by_me === true,
    movesNext: side(body.moves_next),
    waitingOnMySide: body.waiting_on_my_side === true,
    instanceLabel: optStr(body.instance_label),
    quantity: quantityText(body.quantity),
    externalAssetId: optStr(body.external_asset_id),
    internalCheckReference: optStr(body.internal_check_reference),
    shortNotice: body.short_notice === true,
    shortNoticeJustification: optStr(body.short_notice_justification),
    operatorUnattendedAt: optStr(body.operator_unattended_at),
    witnessReleased: body.witness_released === true,
    witnessReleasedAt: optStr(body.witness_released_at),
    itemCount: typeof body.item_count === "number" ? body.item_count : 0,
    attachmentCount: typeof body.attachment_count === "number" ? body.attachment_count : 0,
  };
}

/** The quantity as the server wrote it (a decimal string), "1" when absent. */
function quantityText(raw: unknown): string {
  if (typeof raw === "number") {
    return String(raw);
  }
  if (typeof raw === "string" && raw.trim() !== "") {
    // The server answers a decimal with its scale ("1.000"); trailing zeros say nothing.
    return raw.includes(".") ? raw.replace(/\.?0+$/u, "") : raw;
  }
  return "1";
}

function detail(raw: unknown): RfiDetail {
  const body = record(raw);
  const history = Array.isArray(body.history) ? body.history : [];
  const options = Array.isArray(body.inspector_options) ? body.inspector_options : [];
  const assigneeOpts = Array.isArray(body.assignee_options) ? body.assignee_options : [];
  const known: readonly RfiMove[] = ["ACKNOWLEDGE", "PROPOSE", "CONFIRM"];
  return {
    ...summary(body),
    history: history.map((entry) => {
      const one = record(entry);
      return {
        id: str(one.id),
        at: str(one.at),
        kind: one.kind === "DATE" ? "DATE" : "TRANSITION",
        event: str(one.event),
        fromState: optStr(one.from_state),
        toState: optStr(one.to_state),
        proposedDate: optStr(one.proposed_date),
        text: optStr(one.text),
        actorDisplayName: optStr(one.actor_display_name),
        actingPartyName: optStr(one.acting_party_name),
      };
    }),
    availableMoves: strArray(body.available_moves ?? []).flatMap((one) =>
      known.filter((move) => move === one),
    ),
    inspectorOptions: options.map((entry) => {
      const one = record(entry);
      return { personId: str(one.person_id), displayName: str(one.display_name) };
    }),
    assigneeOptions: assigneeOpts.map((entry) => {
      const one = record(entry);
      return { personId: str(one.person_id), displayName: str(one.display_name) };
    }),
    mayReject: body.may_reject === true,
    mayCancel: body.may_cancel === true,
    mayWaive: body.may_waive === true,
    conductActs: strArray(body.conduct_acts ?? []),
    attachments: (Array.isArray(body.attachments) ? body.attachments : []).map((entry) => {
      const one = record(entry);
      return {
        linkId: str(one.link_id),
        linkPurpose: str(one.link_purpose),
        attachmentId: str(one.attachment_id),
        filename: str(one.filename),
        mimeType: str(one.mime_type),
        fileSizeBytes: typeof one.file_size_bytes === "number" ? one.file_size_bytes : 0,
        uploadedAt: str(one.uploaded_at),
        linkedBy: optStr(one.linked_by),
        classification: parseClassification(one.classification),
      };
    }),
    waiverState: optStr(body.waiver_state),
    waiverReason: optStr(body.waiver_reason),
    waiverProposedAt: optStr(body.waiver_proposed_at),
    waiverProposedByMe: body.waiver_proposed_by_me === true,
    mayReleaseWitness: body.may_release_witness === true,
    witnessReleaseDeclaration: optStr(body.witness_release_declaration),
    mayAssign: body.may_assign === true,
    items: (Array.isArray(body.items) ? body.items : []).map((entry) => {
      const one = record(entry);
      return {
        itpItemId: str(one.itp_item_id),
        itemNo: str(one.item_no),
        instanceLabel: optStr(one.instance_label),
        quantity: quantityText(one.quantity),
        position: typeof one.position === "number" ? one.position : 0,
      };
    }),
    attachmentIds: strArray(body.attachment_ids ?? []),
  };
}

export async function listRfis(
  token: string,
  filters: {
    contractId?: string | null;
    itpId?: string | null;
    fromDate?: string | null;
    toDate?: string | null;
    /** RFI for the contractor's requests only, OPERATOR_REQUEST for the operator's. */
    origin?: "RFI" | "OPERATOR_REQUEST" | null;
    /** One program's requests (its verification type code). */
    verificationTypeCode?: string | null;
    /** Pre-filtered open RFI queue for the signed-in persona. */
    queue?: boolean;
    signal?: AbortSignal;
  } = {},
): Promise<{ total: number; items: RfiSummary[] }> {
  const body = await apiJson(
    token,
    `/rfis${query({
      contract_id: filters.contractId,
      itp_id: filters.itpId,
      from_date: filters.fromDate,
      to_date: filters.toDate,
      origin: filters.origin,
      verification_type_code: filters.verificationTypeCode,
      queue: filters.queue ? true : undefined,
    })}`,
    listBody,
    { signal: filters.signal },
  );
  return { total: body.total, items: body.items.map(summary) };
}

/** Open RFI queue for the signed-in persona (wave L item 5). */
export async function readRfiQueue(
  token: string,
  filters: { contractId?: string | null; signal?: AbortSignal } = {},
): Promise<{ total: number; items: RfiSummary[] }> {
  const body = await apiJson(
    token,
    `/rfis/queue${query({
      contract_id: filters.contractId,
    })}`,
    listBody,
    { signal: filters.signal },
  );
  return { total: body.total, items: body.items.map(summary) };
}

export async function getRfi(
  token: string,
  rfiId: string,
  signal?: AbortSignal,
): Promise<RfiDetail> {
  return apiJson(token, `/rfis/${rfiId}`, detail, { signal });
}

/** One item of a several-item request (RfiRaiseItem), each its own instance. */
export interface RfiRaiseItemPayload {
  itp_item_id: string;
  instance_label: string | null;
  /** A decimal greater than zero, as text so its digits travel exactly; default 1. */
  quantity: string;
}

/** Wave RFI ruling 2: one request names at most this many items (rfi.MAX_REQUEST_ITEMS). */
export const MAX_REQUEST_ITEMS = 20;

/** The raise body on the wire (RfiRaise): what a server refusal names. */
export interface RfiRaisePayload {
  /** Wave RFI rulings 1 and 2: the plan, the checklist category, then the items. */
  itp_id?: string | null;
  checklist_category?: string | null;
  items?: RfiRaiseItemPayload[];
  itp_item_id?: string | null;
  contract_id?: string | null;
  discipline_id?: string | null;
  scope: string;
  proposed_date: string;
  location?: string | null;
  declaration_confirmed: boolean;
  /** Wave INST ruling 1: required on an item RFI (409 rfi.instance_label_required). */
  instance_label?: string | null;
  /** A decimal greater than zero, as text so its digits travel exactly; default 1. */
  quantity?: string;
  external_asset_id?: string | null;
  /** Wave INST ruling 3: the structured declaration. */
  internal_check_reference?: string | null;
  /** Uploaded files, linked to the request as EVIDENCE in the submission itself. */
  attachment_ids?: string[];
  /** Sent only after the server refused the date as short notice (409 rfi.short_notice). */
  short_notice_justification?: string | null;
}

export async function raiseRfi(token: string, payload: RfiRaisePayload): Promise<RfiDetail> {
  return apiJson(token, "/rfis", detail, { method: "POST", body: payload });
}

/** One move on the RFI's date, on the wire (RfiDateMove). */
export interface RfiDateMovePayload {
  move: RfiMove;
  proposed_date: string | null;
  justification: string | null;
  inspector_person_ids: string[];
  assignee_person_id?: string | null;
}

/** The people eligible to conduct this verification under chain and policy. */
export async function listEligibleAssignees(
  token: string,
  rfiId: string,
  signal?: AbortSignal,
): Promise<PersonOption[]> {
  return apiJson(
    token,
    `/rfis/${rfiId}/eligible-assignees`,
    (raw) => {
      if (!Array.isArray(raw)) {
        return [];
      }
      return raw.map((entry) => {
        const one = record(entry);
        return { personId: str(one.person_id), displayName: str(one.display_name) };
      });
    },
    { signal },
  );
}

export async function recordRfiDateMove(
  token: string,
  rfiId: string,
  payload: RfiDateMovePayload,
): Promise<{ move: string; plannedDate: string; agreed: boolean; workflowState: string }> {
  return apiJson(
    token,
    `/rfis/${rfiId}/date-moves`,
    (raw) => {
      const body = record(raw);
      return {
        move: str(body.move),
        plannedDate: str(body.planned_date),
        agreed: body.agreed === true,
        workflowState: str(body.workflow_state),
      };
    },
    { method: "POST", body: payload },
  );
}

/** The operator rejects the contractor's request, saying why (the contractor is told). */
export async function rejectRfi(token: string, rfiId: string, reason: string): Promise<RfiDetail> {
  return apiJson(token, `/rfis/${rfiId}/reject`, detail, { method: "POST", body: { reason } });
}

/** The contractor cancels the request while not yet agreed or conducted. */
export async function cancelRfi(token: string, rfiId: string, reason?: string): Promise<RfiDetail> {
  return apiJson(token, `/rfis/${rfiId}/cancel`, detail, {
    method: "POST",
    body: { reason: reason?.trim() ? reason.trim() : null },
  });
}

/**
 * The waiver, in two hands (wave INST ruling 4): the operator's call proposes it with its
 * reason (PENDING_AUTHORITY), the engineering authority's call on the same route
 * completes it. The reason is required on both (422 rfi.waiver_reason_required): the
 * server is the judge, so a blank reason is sent as null and refused in its words.
 */
export async function waiveRfi(token: string, rfiId: string, reason?: string): Promise<RfiDetail> {
  return apiJson(token, `/rfis/${rfiId}/waive`, detail, {
    method: "POST",
    body: { reason: reason?.trim() ? reason.trim() : null },
  });
}

/** The body for delegating conduct of an RFI to another eligible principal. */
export interface RfiDelegatePayload {
  assignee_person_id: string;
  reason?: string | null;
}

/** The current assignee delegates conduct to another conduct-eligible principal. */
export async function delegateRfi(
  token: string,
  rfiId: string,
  payload: RfiDelegatePayload,
): Promise<RfiDetail> {
  return apiJson(token, `/rfis/${rfiId}/delegate`, detail, {
    method: "POST",
    body: payload,
  });
}

/**
 * Wave COM item 3: the contractor releases a witness point the operator did not attend,
 * with its own declaration of what was inspected and found and the evidence (uploaded
 * first, sent by id). The server judges every refusal: 409 rfi.witness_not_released
 * names the deadline while the operator still has time, 409
 * rfi.hold_point_never_released on a hold point, 422 rfi.release_declaration_required.
 */
export async function releaseWitness(
  token: string,
  rfiId: string,
  payload: { declaration: string | null; attachment_ids: string[] },
): Promise<RfiDetail> {
  return apiJson(token, `/rfis/${rfiId}/release-witness`, detail, {
    method: "POST",
    body: payload,
  });
}

/**
 * Wave RFI ruling 4: the planner names, or changes, the one assignee until conduct
 * starts; conduct START is then refused to anyone else (403 rfi.not_assignee).
 */
export async function assignRfi(
  token: string,
  rfiId: string,
  payload: { assignee_person_id: string; reason: string | null },
): Promise<RfiDetail> {
  return apiJson(token, `/rfis/${rfiId}/assign`, detail, { method: "POST", body: payload });
}
